import endpoint from "#src/config/endpoint";
import request from "#src/utils/request";
import type {
  IDeviceActivityItem,
  IDeviceDriveFile,
  IDeviceDriveFileSnapshot,
  IDevicePolicyDetailItem,
  IDeviceSoftwareItem,
  IDeviceSoftwareStatistics,
  IDeviceStatistics,
  IEndpointDevice,
  ISoftwareItem,
} from "./types";
import type { IPaginatedResponse, IPaginateParams } from "#src/@types/api";

interface IListDeviceParams {
  sort: string | undefined;
  os: string | undefined;
  host_status: string | undefined;
  tag: string | undefined;
  policy_compliance: string | undefined;
  policy_compliance_id: string | undefined;
  policy_compliance_status: string | undefined;
  program_id: string | undefined;
  asset_type: string | undefined;
  backup_enabled: string | undefined;
}

export async function listDevices(
  workspaceId: string,
  params: IListDeviceParams & IPaginateParams
): Promise<IPaginatedResponse<IEndpointDevice>> {
  return await request.get<never, IPaginatedResponse<IEndpointDevice>>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts`,
    { params }
  );
}

export async function listDevicesNoPaging(
  workspaceId: string,
  params: IListDeviceParams & { q: string | undefined }
): Promise<IEndpointDevice[]> {
  return await request.get<never, IEndpointDevice[]>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts`,
    { params: { ...params, paging: 0 } }
  );
}

/** @deprecated Legacy API. We don't show devices' statistics anymore (08/04/2025) */
export function retrieveDeviceStatistics(workspaceId: string) {
  return request.get<never, IDeviceStatistics>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/statistics`
  );
}

export async function retrieveDeviceDetails(
  workspaceId: string,
  hostId: number
) {
  return request.get<never, IEndpointDevice>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${hostId}`
  );
}

export function listDeviceActivities(
  workspaceId: string,
  deviceId: number,
  params: {
    type?: string;
  } & IPaginateParams
) {
  return request.get<never, IPaginatedResponse<IDeviceActivityItem>>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${deviceId}/activities`,
    { params }
  );
}

export function listDevicePolicies(
  workspaceId: string,
  deviceId: number,
  params: {
    policy_compliance_status: string | undefined;
  } & IPaginateParams
) {
  return request.get<never, IPaginatedResponse<IDevicePolicyDetailItem>>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${deviceId}/policy_compliance`,
    { params }
  );
}

export function listDeviceSoftwares(
  workspaceId: string,
  deviceId: number,
  params: {
    vulnerable: string | undefined;
    sources?: string;
  } & IPaginateParams
) {
  return request.get<never, IPaginatedResponse<IDeviceSoftwareItem>>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${deviceId}/programs`,
    { params }
  );
}

/** @deprecated not used anywhere. */
export function retrieveSoftware(workspaceId: string, softwareId: string) {
  return request.get<never, ISoftwareItem>(
    `${endpoint.WORKSPACES}/${workspaceId}/software/${softwareId}`
  );
}

export function retrieveDeviceSoftwareStatistics(
  workspaceId: string,
  deviceId: string
) {
  return request.get<never, IDeviceSoftwareStatistics>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${deviceId}/programs/statistic`
  );
}

export function updateHost(
  workspaceId: string,
  hostId: number,
  data: { name?: string; tags?: string[]; asset_type?: string }
) {
  return request.put<never, { success: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${hostId}`,
    data
  );
}

export function shutdownHost(workspaceId: string, hostId: number) {
  return request.post<never, { success: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${hostId}/shutdown`
  );
}

export function restartHost(workspaceId: string, hostId: number) {
  return request.post<never, { success: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${hostId}/restart`
  );
}

export function remoteHost(
  workspaceId: string,
  hostId: number,
  data?: { close: true }
) {
  return request.post<never, { success: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${hostId}/remote`,
    data
  );
}

export function uninstallHost(workspaceId: string, hostId: number) {
  return request.post<never, { success: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${hostId}/uninstall`
  );
}

export function countHosts(
  workspaceId: string,
  params: {
    program_id: string;
  }
) {
  return request.get<never, { count: number }>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/count`,
    { params }
  );
}

export function createInstallAgent(
  workspaceId: string,
  data: {
    gender: string;
    fullname: string;
    phone_number: string;
    // TODO: should be an enum
    job_title: string;
    industry: string;
    // TODO: should be an enum
    employee_size: string;
    email: string;
  }
) {
  return request.post<never, { success: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/install_agent`,
    data
  );
}

export function retrieveInstallAgent(workspaceId: string) {
  return request.get<never, { requested: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/install_agent`
  );
}

export function archiveHost(workspaceId: string, hostId: number) {
  return request.post<never, { success: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${hostId}/archive`
  );
}

export function enableDeviceBackup(
  workspaceId: string,
  deviceId: number,
  backup_enabled: boolean
) {
  return request.put<never, { success: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${deviceId}/backup`,
    { backup_enabled }
  );
}

export function enableMultipleDeviceBackup(
  workspaceId: string,
  deviceIds: number[],
  backup_enabled: boolean
) {
  return request.post<never, { success: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/multiple_backup`,
    { backup_enabled, ids: deviceIds }
  );
}

export async function retrieveDeviceDrivePath(
  workspaceId: string,
  deviceId: string
) {
  return request.get<never, { drive_path: string }>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${deviceId}/drive_path`
  );
}

export async function listDeviceDrives(
  workspaceId: string,
  deviceId: string,
  params: {
    cur_ext: string | undefined;
    sensitivity_level_id: string | undefined;
    content_type_id: string | undefined;
    cur_path: string | undefined;
    cur_name: string | undefined;
  }
): Promise<IDeviceDriveFile[]> {
  return request.get<never, IDeviceDriveFile[]>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${deviceId}/drive`,
    { params }
  );
  // return [
  //   {
  //     id: "17c2dd3a-5e0b-4517-8c3a-7d0f8d5c4457",
  //     internal_agent_id:
  //       "IQxhEmzWRBD3M3iRB6deDBVrNas0VRZXeyf5yJrJezbMiyYWLLg2sqluIFGJuB8V",
  //     origin_path: "E:/test1.txt",
  //     cur_path: "E:/test1.txt",
  //     cur_name: "test1.txt",
  //     cur_ext: ".txt",
  //     total_size: 0,
  //     sensitivity_level: [],
  //     content_types: [],
  //   },
  //   {
  //     id: "2b9d2795-1f93-4463-8acf-c854aaa54a76",
  //     internal_agent_id:
  //       "IQxhEmzWRBD3M3iRB6deDBVrNas0VRZXeyf5yJrJezbMiyYWLLg2sqluIFGJuB8V",
  //     origin_path: "E:/abcd/New Microsoft Word Document.docx",
  //     cur_path: "E:/abcd/New Microsoft Word Document.docx",
  //     cur_name: "New Microsoft Word Document.docx",
  //     cur_ext: ".docx",
  //     total_size: 0,
  //     sensitivity_level: [],
  //     content_types: [],
  //   },
  //   {
  //     id: "91b1e3c6-3f5d-11f0-abc3-00ff413b0f766jI1X9H0",
  //     internal_agent_id:
  //       "IQxhEmzWRBD3M3iRB6deDBVrNas0VRZXeyf5yJrJezbMiyYWLLg2sqluIFGJuB8V",
  //     origin_path: "E:/abc.txt",
  //     cur_path: "E:/abc.txt",
  //     cur_name: "",
  //     cur_ext: ".txt",
  //     total_size: 0,
  //     sensitivity_level: [],
  //     content_types: [],
  //   },
  //   {
  //     id: "ae273601-3f5d-11f0-abc3-00ff413b0f766jI1X9H0",
  //     internal_agent_id:
  //       "IQxhEmzWRBD3M3iRB6deDBVrNas0VRZXeyf5yJrJezbMiyYWLLg2sqluIFGJuB8V",
  //     origin_path: "E:/abcd.txt",
  //     cur_path: "E:/abcd.txt",
  //     cur_name: "",
  //     cur_ext: ".txt",
  //     total_size: 0,
  //     sensitivity_level: [],
  //     content_types: [],
  //   },
  // ];
}

export async function updateFileLabels(
  workspaceId: string,
  deviceId: string,
  fileId: string,
  sensitivityId: string,
  contentIds: number[]
) {
  return request.put<never, any>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${deviceId}/files/${fileId}/data_labels`,
    { sensitivity_id: sensitivityId, content_ids: contentIds }
  );
}

/** @todo it might be that this is not needed. */
export async function deleteFileLabels(
  workspaceId: string,
  deviceId: string,
  fileId: string,
  contentIds: number[]
) {
  return request.post<never, any>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${deviceId}/files/${fileId}/data_labels/delete`,
    { content_ids: contentIds }
  );
}

export async function listFileSnapshots(
  workspaceId: string,
  deviceId: string,
  fileId: string
) {
  return request.get<never, IDeviceDriveFileSnapshot>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${deviceId}/files/${fileId}/snapshots`
  );

  // return {
  //   key: "RBKEgUzOA9WLFtTIp3sc1CaBB5ZAOCas2PTvGd8sl4NjSV8t9U/125a83ea-2f2c-471d-bf62-1ed95388d6b2",
  //   file_versions: [
  //     {
  //       version_id: "alhfZjxP1kyq6JlA4GQh8aqWFFHJiHAj",
  //       size: 14,
  //       last_modified: "2025-05-20T08:48:53Z",
  //     },
  //     {
  //       version_id: "YJqCMJqeWUzeMAdrpZq303Op9CvfHtA1",
  //       size: 11,
  //       last_modified: "2025-05-20T08:48:15Z",
  //     },
  //   ],
  //   metadata: {
  //     last_modified: "2025-05-20T08:48:53Z",
  //     size: 14,
  //   },
  //   last_modified: "2025-05-20T08:48:53Z",
  // };
}

export async function restoreFileSnapshot(
  workspaceId: string,
  deviceId: string,
  fileId: string,
  versionId: string
) {
  return request.post<never, { success: boolean }>(
    `${endpoint.WORKSPACES}/${workspaceId}/hosts/${deviceId}/files/${fileId}/snapshots/${versionId}/restore`
  );
}
